/**
 * @file Inline typo-correction for common email-provider domains.
 * @module @labcraft/www/ui/email-typos
 *
 * Catches the textbook "I mistyped my email" cases — `gnail.com`,
 * `gmial.com`, `outlok.com`, `yhoo.com` — that a real-world contact
 * form sees often enough to be worth a small client-side hint. Not a
 * substitute for delivery verification; we explicitly chose not to
 * RCPT-probe or call a third-party email-validation service (see
 * `.agents/memory/...` and the contact-form scope discussion). The
 * hint is non-blocking — a user with a non-typo'd uncommon-domain
 * address can ignore the suggestion and submit anyway.
 *
 * Algorithm: split the input on the last `@`, compare the typed
 * domain against a curated list of common provider domains via
 * Levenshtein distance, return the closest match if it sits within
 * the chosen edit-distance budget (default 2 — catches one swap, one
 * insert, one delete, one substitution; transpositions register as 2
 * under classic Levenshtein, so 2 is the minimum useful threshold).
 *
 * The function is pure (no React, no DOM, no fetches), so the consumer
 * decides when to call it: per-keystroke is fine on modern hardware
 * given the domain list is short, but per-blur is also reasonable.
 */

/**
 * Common email-provider domains we'll suggest corrections toward.
 * Curated rather than exhaustive — these are the domains where typos
 * are common enough that a wrong suggestion (false positive) is rare,
 * and a correct suggestion (true positive) is high-value.
 *
 * Don't expand this list to less-common providers without thinking
 * about false positives: any user whose real domain is Levenshtein-2
 * from a list entry will be wrongly nudged.
 */
const COMMON_DOMAINS: readonly string[] = [
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'outlook.co.uk',
  'hotmail.com',
  'hotmail.co.uk',
  'live.com',
  'yahoo.com',
  'yahoo.co.uk',
  'yahoo.co.in',
  'icloud.com',
  'me.com',
  'mac.com',
  'proton.me',
  'protonmail.com',
  'pm.me',
  'fastmail.com',
  'fastmail.fm',
  'aol.com',
  'duck.com',
  'msn.com',
]

/** Maximum edit distance for which a correction is suggested. */
const MAX_DISTANCE = 2

/**
 * Return a suggested corrected email when the typed domain is a
 * near-match to a known common provider; `null` when the typed
 * domain is already a known provider, or no near match is found, or
 * the input isn't shaped like an email yet.
 *
 * @param input - The current contents of the email input. Trimmed by
 *   the caller; this function only inspects the `@…` portion.
 * @returns Suggested correction (e.g. `'ada@gmail.com'`) or `null`.
 */
export function suggestEmailCorrection(input: string): string | null {
  const at = input.lastIndexOf('@')
  if (at < 1 || at === input.length - 1) return null
  const local = input.slice(0, at)
  const domain = input.slice(at + 1).toLowerCase()

  if (COMMON_DOMAINS.includes(domain)) return null

  let best: { d: string; dist: number } | null = null
  for (const candidate of COMMON_DOMAINS) {
    const dist = levenshtein(domain, candidate)
    // dist === 0 is filtered by the includes() check above; require a
    // positive distance to avoid suggesting the input back at itself
    // in edge cases.
    if (dist > 0 && dist <= MAX_DISTANCE && (!best || dist < best.dist)) {
      best = { d: candidate, dist }
    }
  }
  if (!best) return null
  return `${local}@${best.d}`
}

/**
 * Classic Levenshtein edit distance — number of single-character
 * insertions, deletions, or substitutions required to turn `a` into
 * `b`. Transpositions cost 2 (one delete + one insert), so
 * `MAX_DISTANCE = 2` is the threshold that picks up the
 * `gmial`→`gmail` swap class.
 *
 * Iterative two-row DP, `O(a*b)` time, `O(b)` space.
 */
function levenshtein(a: string, b: string): number {
  if (a === b) return 0
  if (a.length === 0) return b.length
  if (b.length === 0) return a.length

  let prev = Array.from({ length: b.length + 1 }, (_, i) => i)
  const curr = new Array<number>(b.length + 1)
  for (let i = 0; i < a.length; i++) {
    curr[0] = i + 1
    for (let j = 0; j < b.length; j++) {
      const cost = a[i] === b[j] ? 0 : 1
      curr[j + 1] = Math.min(curr[j]! + 1, prev[j + 1]! + 1, prev[j]! + cost)
    }
    prev = curr.slice()
  }
  return prev[b.length]!
}
